import { Dictionary, Utilities } from '../../helper/util';
import { DateTimeHelper } from '../../helper/datetime.helper';
import { IUploadConfig } from '../content-upload/content-upload.component';
import { AlertController, NavController, ToastController } from '@ionic/angular';
import { Component, Output, Input, EventEmitter, OnChanges, SimpleChanges, ViewChild, ViewChildren, QueryList, ElementRef, ContentChild, TemplateRef } from '@angular/core';
import { BrowserHelper } from '../../helper/browser.helper';
import { Upload } from '../../data/schema/upload';
import { Log } from '../../log';

/**
 * Generated class for the DataTableComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */

export enum AddItemType {
  NONE = 0, //won't display any add option; this is default
  INLINE = 1, //allows inline addition; ie. the datatable will handle the addition of a new item
  BUTTON = 2, //use if you want to handle the addition of an item separately
}

export enum MediaOptions {
  PDF = '.pdf',
  CSV = '.csv',
  PNG = '.png',
  JPG = '.jpg',
  JPEG = '.jpeg'
}

export enum ColumnType {
  TEL = 'tel',
  URL = 'url',
  DATE = 'date',
  TIME = 'time',
  TEXT = 'text',
  EMAIL = 'email',
  NUMBER = 'number',
  SELECT = 'select',
  CONTENT = 'content',
  CURRENCY = 'currency',
  PERCENT = 'percent',
  CHECKBOX = 'checkbox',
  BUTTON = 'button',
  BUTTONARRAY = 'button-array',
  TEXTARRAY = 'text-array',
  BOOLEAN = 'boolean',
  ICON = 'icon'
}

@Component({
  selector: 'data-table',
  templateUrl: 'data-table.html',
  styleUrls: ['data-table.scss']
})
export class DataTableComponent implements OnChanges {

  @Output() itemSelected: EventEmitter<any> = new EventEmitter();
  @Output() addItemClicked: EventEmitter<any> = new EventEmitter();
  @Output() itemAdded: EventEmitter<any> = new EventEmitter();
  @Output() inputBlur: EventEmitter<any> = new EventEmitter(); // used for text input edits
  @Output() itemsSorted: EventEmitter<void> = new EventEmitter();
  @Output() rowButtonClicked: EventEmitter<any> = new EventEmitter();
  @Output() itemChanged: EventEmitter<IItemChange> = new EventEmitter();
  @Output() itemArchived: EventEmitter<any> = new EventEmitter();
  @Output() mediaUploaded: EventEmitter<any> = new EventEmitter();
  @Output() mediaCleared: EventEmitter<any> = new EventEmitter();
  @Output() clipboardCopied: EventEmitter<any> = new EventEmitter();
  @Output() didSearch: EventEmitter<string> = new EventEmitter();
  @Output() didFilter: EventEmitter<string> = new EventEmitter();

  @Input() tableOptions: DataTableOptions = new DataTableOptions();
  @Input() busy = false;
  @Input() tableWidth = '100%';

  @ViewChildren('dateInput') date?: QueryList<ElementRef>;
  @ViewChild('newDateInput') newDate?: ElementRef;

  sortBy?: string;
  sortAsc = false;
  reorder = false;
  newItem: any = {};
  searchFilter?: string;
  filteredDataSet: any[] = [];
  clearedImages: number[] = [-8]; //-8 is for adding a new item
  todayDateFormat = DateTimeHelper.formatDateShort();
  headersExpanded = false;
  isScrolledX = false;

  onTableScroll(event: Event) {
    this.isScrolledX = (event.target as HTMLElement).scrollLeft > 0;
  }

  // this is a dictionary of primary key to object
  pkSelected: Dictionary<any> = {};
  // dictionary for items in edit mode
  editItemDic: Dictionary<any> = {};

  totalRows: number[] = [];

  @ContentChild(TemplateRef) customFilterTemplate: TemplateRef<any>;

  @ContentChild(TemplateRef) customLastColTemplate: TemplateRef<any>;

  private sortByHeader?: ITableHeader;

  // dictionary mapping available filters to their selected value
  filtersSelected: Dictionary<string> = {}
  currFilter:string;

  constructor(
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private browserHelper: BrowserHelper,
    protected navCtrl: NavController
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    // Log.debug('datatable changes', changes);
    if (this.tableOptions != null) {
      //Log.debug('  - options not null, filter');
      if (this.tableOptions.filters != null) {
        this.tableOptions.filters.forEach(f => {
          this.filtersSelected[f.field] = f.default;
        })
      }
      this.filterDataset();
    } else if (this.tableOptions == null) {
      //Log.debug('  - no options, initialize with blank');
      this.tableOptions = new DataTableOptions();
    }
    if (this.searchFilter != null) {
      //Log.debug('  - search filter not null, re-apply');
      this.search();
    }
    //Log.debug('  - run auto-sort');
    this.autosort();
  }

  refresh() {
    this.filterDataset();
  }

  pickDate(index: number) {
    const inputArray = this.date?.toArray() || [];
    // Log.debug('date inputs', inputArray)
    if (inputArray.length > 1) {
      inputArray[index].nativeElement.showPicker();
    } else {
      inputArray[0].nativeElement.showPicker();
    }
  }

  getPkSelected() {
    const selectedPks = [];
    for (const [key, value] of Object.entries(this.pkSelected)) {
      if (value === true) {
        selectedPks.push(+key);
      }
    }
    return selectedPks;
  }

  clearPkSelection() {
    this.pkSelected = {};
  }

  onBlur(pk: number, evt: any) {
    const val = evt.target.value;
    this.inputBlur.emit(new DataTableBlurResp({
      primaryKey: pk,
      value: val
    }));
  }

  applySort(headField: string, asc: boolean) {
    // WAS CALLING THIS FROM VARIOUS PAGES, NOW HANDLING WITH LOCALSTORAGE SAVING SORT ORDER ME THINKS
    //this.sort(this.sortByHeader, false);
    const head = this.tableOptions.headers.find(c => { return c.field == headField; });
    // set same field as prev sort so our sort order is respected
    this.sortBy = headField;
    this.sortAsc = !asc; // sort func flips it like it was clicked again, so negate it
    this.sort(head);
  }

  uploadMedia(event: any) {
    const media: any[] = [];
    if (!event || event.target.files.length === 0) { return; }
    Log.debug('upload event emitter', event.target.files);
    const myReader: FileReader = new FileReader();
    // Attempt to read the base64 for the asset
    myReader.readAsDataURL(event.target.files[0]);
    myReader.onloadend = (async _ => {
      media.push(myReader.result?.toString());
      this.mediaUploaded.emit(media);
    });
  }

  sort(column: ITableHeader) {
    if (column == null || column.sortable !== true) { return; }

    // check if same column clicked, if so, reverse sort order, else default to ascending
    if (this.sortBy === column.field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortAsc = true;
    }

    // store selected sort on specific page for specific table so we can auto-sort on refresh
    const key = window.location.href.substring(window.location.href.lastIndexOf('/') + 1) + this.tableOptions.type;
    localStorage.setItem(key, JSON.stringify(column));
    localStorage.setItem(key + '-order', this.sortAsc ? '1' : '0');

    this.doSort(column, this.sortAsc);
  }

  autosort() {
    const key = window.location.href.substring(window.location.href.lastIndexOf('/') + 1) + this.tableOptions.type;
    const col = localStorage.getItem(key);
    const asc = localStorage.getItem(key + '-order');
    if (col == null) { return; }
    this.doSort(JSON.parse(col) as ITableHeader, asc === '1' ? true : false);
  }

  doSort(column: ITableHeader, sortAscending: boolean) {
    Log.debug('sorting in ascending ' + sortAscending, column);
    this.sortByHeader = column;
    this.sortBy = column.field;
    this.tableOptions.dataSet.sort((a, b) => { return this.sortFunc(a, b, sortAscending, column.isDate, column.isNumber) });
    this.filteredDataSet.sort((a, b) => { return this.sortFunc(a, b, sortAscending, column.isDate, column.isNumber) });
  }

  private sortFunc(a, b, sortAscending, isDate: boolean = false, isNumber: boolean = false) {
    // Grab the fields we wish to sort by
    let valA = a[this.sortBy];
    let valB = b[this.sortBy];
    // Lowercase strings so it's a case insensitive sort
    if (isNumber || (this.sortBy === 'landingNumbers' && valA.match(/\d+/g) && valB.match(/\d+/g))) {
      if (typeof valA === 'string' && typeof valB === 'string') { // if number is in string format
        // replace non alphanumeric chars
        valA.replace(/[\W_]+/g, ""); // https://stackoverflow.com/a/20864946/7015266
        valB.replace(/[\W_]+/g, "");
        return sortAscending ? parseInt(valB) - parseInt(valA) : parseInt(valA) - parseInt(valB);
      }
      return sortAscending ? valB - valA : valA - valB;
    } else if (isDate) {
      valA = new Date(valA);
      valB = new Date(valB);

      if (isNaN(valA)) valA = null;
      if (isNaN(valB)) valB = null;
    } else {
      if (typeof valA === 'string') {
        valA = valA.toLowerCase();
      }
      if (typeof valB === 'string') {
        valB = valB.toLowerCase();
      }
    }
    // Run the sort logic
    if (valA < valB || valA == null) {
      return (sortAscending ? -1 : 1);
    } else if (valA > valB || valB == null) {
      return (sortAscending ? 1 : -1);
    } else {
      if (valA == null && valB == null) {
        return 0
      }
      //return 0;
      // if equal, sort by primary key to ensure consistent ordering
      return a[this.tableOptions.primaryKey] - b[this.tableOptions.primaryKey]
    }
  }

  reorderItems(event: any) {
    const indexes = event.detail;
    // figure out positions
    const from = indexes.from;
    const to = indexes.to;

    // Update position
    const element = this.tableOptions.dataSet[from];
    this.tableOptions.dataSet.splice(from, 1);
    this.tableOptions.dataSet.splice(to, 0, element);

    event.detail.complete();

    // Save them
    this.itemsSorted.emit();

    //this.refresh();
  }

  selectByPk(item: any) {
    this.itemSelected.emit(item);
  }

  rowButtonClick(event: Event, item: any) {
    event.stopPropagation();
    this.rowButtonClicked.emit(item);
  }

  columnClick(item: any) {
    this.tableOptions.clickableColumnCallback(item);
    //this.navCtrl.navigateForward(this.tableOptions.columnClickRoute + item[this.tableOptions.primaryKey ?? '']);
  }

  columnClick2(item: any) {
    this.tableOptions.clickableColumnCallback(item);
    // this.navCtrl.navigateForward(this.tableOptions.columnClickRoute + item.jobId);
  }

  // this just avoids needing to keep reference to passed in filter, since in most cases there's
  // likely only 1, or if multiple, they have unique values
  applyFilterSimple(val: string){
    for(let f of this.tableOptions.filters ){
      for(let o of f.values){
        if (o == val) {
          this.filtersSelected[f.field] = val;
          this.filterDataset(f);
          return;
        }
      }
    }
  }

  applyFilter(filter: ITableFilter, val: string){
    this.filtersSelected[filter.field] = val;
    this.filterDataset(filter)
  }

  filterDataset(filter?: ITableFilter) {
    // start with full set
    this.filteredDataSet = this.tableOptions.dataSet;

    // run through external filter if provided
    if (filter?.changeCallback != null) {
      filter.changeCallback(this.filtersSelected[filter.field]);
    }

    if ( filter?.matchFunc != null ) {

    }

    // new way to apply filters is to iterate through the filters and continue to whittle down the filtered set
    if (this.tableOptions.filters != null) {
      this.tableOptions.filters.forEach(tableFilter => {
        if (this.filtersSelected[tableFilter.field]) {

          if ( tableFilter.matchFunc != null ){
            // in this case we are actually using a function on the data set item object to
            // determine if it matches the filter or not
            this.filteredDataSet = this.filteredDataSet.filter(dataSetItem => {
              return dataSetItem[tableFilter.matchFunc](this.filtersSelected[tableFilter.field].toLowerCase());
            });
          } else {
            this.filteredDataSet = this.filteredDataSet.filter(dataSetItem => {
              if (tableFilter.partialMatch) {
                return (dataSetItem[tableFilter.field]?.toLowerCase().includes(this.filtersSelected[tableFilter.field].toLowerCase()))
              } else {
                return (dataSetItem[tableFilter.field] === this.filtersSelected[tableFilter.field])
              }
            });
          }
        }
      });
    }

    // then if we have any search string entered, filter by that as well
    if (!Utilities.stringEmpty(this.searchFilter)) {
      Log.debug('apply search filter')
      this.search();
    }

    if (this.tableOptions.includePkCheckbox && this.tableOptions.primaryKey != null) {
      // if checkboxes enabled, also require the primary key on data objects, then init checkbox to unselected
      this.filteredDataSet.forEach(m => {
        this.pkSelected[m[this.tableOptions.primaryKey ?? '']] = false;
      });
    }

    if (filter != null) {
      this.currFilter = this.filtersSelected[filter.field];
      this.didFilter.emit(this.filtersSelected[filter.field]);
    }
  }

  openUrl(url: string) {
    this.browserHelper.openUrl(url);
  }

  // should only be called from filter function 
  private search() {
    const filter = this.searchFilter?.toLowerCase();
    this.filteredDataSet = this.filteredDataSet.filter(m => {
      for (const field of this.tableOptions.searchFields) {
        let val = m[field];
        if (val != null) {
          if (typeof val === 'number') {
            val = val + '';
          }
          if (val.toLowerCase().includes(filter)) {
            return true;
          }
        }
      };
      return false;
    });
    this.didSearch.emit(filter);
  }

  runCustomClick(event: any) {
    if (this.tableOptions.includePkCheckbox) {
      // if we have checkboxes, lets convert selected dictionary to array of pk's
      const selectedPks = [];
      for (const [key, value] of Object.entries(this.pkSelected)) {
        if (value === true) {
          selectedPks.push(+key);
        }
      }
      event(selectedPks);
      this.pkSelected = {};
    } else {
      event();
    }
  }

  updateDateFormat(event: any, name: string) {
    this.todayDateFormat = DateTimeHelper.formatDateShort(event.target.value);
  }

  addBtnClicked() {
    this.addItemClicked.emit();
  }

  addItem() {
    // makes checks for required before emiting new item
    let missing = '';
    this.tableOptions.columns.forEach((col, i) => {
      if (col.isRequired && !this.newItem[col.classProperty] &&
        (typeof this.newItem[col.classProperty] === 'number' && this.newItem[col.classProperty] === 0 ||
          col.type !== ColumnType.CONTENT && Utilities.stringEmpty(this.newItem[col.classProperty])) ||
        (col.type === ColumnType.CONTENT && this.clearedImages.includes(-8))) {
        missing += `• ${this.tableOptions.headers[i].label} is required<br/>`;
      }
    });
    if (missing.length > 0) {
      this.displayAlert('Missing Fields', missing);
    } else {
      if (this.inputsValid()) {
        this.itemAdded.emit({ ...this.newItem });
      }
    }

  }

  resetAddItem() {
    this.tableOptions.columns.forEach((col) => {
      if (col.type === ColumnType.CONTENT) {
        this.newItem[col.classProperty] = [];
      } else if (col.type === ColumnType.CHECKBOX) {
        this.newItem[col.classProperty] = false;
      } else if (this.newItem[col.classProperty] !== 'function') {
        this.newItem[col.classProperty] = null;
      }
    });
    Log.debug('After resetting', this.newItem);
  }

  editItem(index: number) {
    if (this.inputsValid(index)) {
      this.itemChanged.emit({
        index,
        newValue: this.editItemDic[index],
        oldValue: this.tableOptions.dataSet[index]
      });
      delete this.editItemDic[index];
    }
  }

  toggleEdit(index: number) {
    // remove copied item for dictionary if they cancel edit
    if (this.editItemDic[index]) {
      delete this.editItemDic[index];
    } else {
      this.editItemDic[index] = { ...this.tableOptions.dataSet[index] };
    }
    Log.debug('Items to edit', this.editItemDic);
  }

  archiveItem(index: number) {
    Log.debug('Archived Index', index);
    this.itemArchived.emit(index);
  }

  selectedFile(file: Upload, index?: number) {
    if (file) {
      Log.debug('Image selected', file);
      const imgIndex = this.clearedImages.findIndex(i => i === (index != null ? index : -8));
      this.clearedImages.splice(imgIndex, 1);
      Log.debug('cleared images', this.clearedImages)
      this.mediaUploaded.emit({ file, index });
    }
  }

  clearImage(index?: number) {
    const indexToAdd = index != null ? index : -8;
    this.clearedImages.push(indexToAdd);
    this.mediaCleared.emit({ index });
    Log.debug('cleared images', this.clearedImages)
  }

  getColumnTotal(column: any) {
    if (this.tableOptions.totalColumns.includes(column)) {
      return this.tableOptions.dataSet
        .reduce((prevVal, currVal) => prevVal + +currVal[column], 0);
    }
    return '';
  }

  getOptionDisplayValue(colName: string, value: any) {
    if (value) {
      const column = this.tableOptions.columns.find(c => c.classProperty === colName);
      if (column) {
        return column.options.find(op => op.value == value)?.displayValue ?? value;
      } else {
        return value;
      }
    }
    return '';
  }

  private inputsValid(index?: number) {
    const content = this.tableOptions.columns.find(col => col.type === ColumnType.CONTENT);
    const isFormValid = document.getElementsByClassName('invalid-el').length === 0;
    if (!isFormValid) {
      this.displayAlert('Form Errors', 'Please correct the form errors first.');
    } else if (index != null && content && content.isRequired && this.clearedImages.includes(index)) {
      const colIndex = this.tableOptions.columns.findIndex(col => col.type === ColumnType.CONTENT)
      this.displayAlert('Missing Information', `Upload a ${this.tableOptions.headers[colIndex].label}`);
      return false;
    }
    return isFormValid;
  }

  private async displayAlert(title: string, message: string) {
    const alert = await this.alertCtrl.create({
      header: title,
      message,
      buttons: ['OK']
    });
    alert.present();
    return alert;
  }

  async copyToClipboard(value: any, colName: any) {
    await navigator.clipboard.writeText(value.toString());
    this.clipboardCopied.emit(colName);
    const toast = await this.toastCtrl.create({
      duration: 3000,
      position: 'top',
      message: `${colName} copied to clipboard.`
    });
    await toast.present();
  }

}

export class DataTableBlurResp {
  primaryKey?: number;
  value?: string;
  constructor(init?: DataTableBlurResp) {
    Object.assign(this, init);
  }
}

export class DataTableOptions {
  type?: string;
  addBtnLabel?: string;
  includeRowButton?: boolean = false;
  includeLastColTemplate?: boolean = false;
  rowButtonHeader?: string = '';
  includePkCheckbox?: boolean = false;
  allowEdit?: boolean = false;
  allowDelete?: boolean = false;
  primaryKey?: string;
  rowButtonText?: string = 'Select';
  showReorder?: boolean = false;
  showSearch?: boolean = false;
  addType?: AddItemType = AddItemType.NONE;
  showImportButton?: boolean = false;
  showTableHeader?: boolean = false;
  tableHeader?: string = 'Table Header';
  enableClickableColumn?: boolean = false; //if specified, a clickable column with an index will be 
  enableClickableColumnForCheckCol?: boolean = false;
  clickableColumn?: number = 0; //index of column you want to be clickable
  clickableColumnCallback?: any; //index of column you want to be clickable
  columnClickRoute?: string = ''; //use if you want to direct the user to a different page when an item is clicked
  mediaOptions?: MediaOptions[] | string; //this is currently used to specify file types for the import button
  headers: ITableHeader[] = [];
  columns: ITableColumn[] = [];
  dataSet: any[] = [];
  searchFields: string[] = []; //fields in object that can be searched
  filters?: ITableFilter[] = []; //use if you want to allow the user to filter the dataset
  customButtons?: ICustomButton[] = []; //use if you want add custom buttons other than the current functionality
  noItemsMessage?: string = 'No items found'; //message to show if dataset is empty
  indicateNewItems?: boolean = false;
  newItemLookUp?: string = 'isNew';
  showTotalRow?: boolean = false; //determines wether to show column totals for the table contents
  totalColumns?: any[] = []; //determines which columns to sum up
  bgProp?: string;
  tableCssClass?: string; //if specified will add custom css to whole table
  propertyForDisabled?: string;
  contentUploadConfig?: IUploadConfig; //used when the input type is CONTENT
  showHeaderExpand?: boolean = false; //shows toggle button to expand/collapse truncated column headers

  includePagination?: boolean = false; //if specified will paginate the results shown in the table
  paginationItemsPerPage?: number = 15; //number of items to show at a time
  paginationCurrentPage?: number = 1; //index of list to be displayed first
  paginationTotalItems?: number; //total number of items in the list


  constructor(init?: DataTableOptions) {
    Object.assign(this, init);
    if (this.mediaOptions) { this.mediaOptions = this.mediaOptions.toString(); }
  }
}

export interface ITableHeader {
  label?: string; //the column title displayed to the user
  field?: string; //the field on the object to be displayed (used for the filtering)
  sortable?: boolean; //determines if column will be considered whiles sorting the table contents
  isLocked?: boolean; //shows lock icon and lock message
  isNumber?: boolean; // causes right alignment
  isDate?: boolean;
  helpText?: string; //shows help icon with click handler to display help text
  minWidth?: string; //specifies the min width of the column eg '100px'
  sticky?: boolean; //freezes the column at left: 0 for horizontal scroll
}

export class ITableColumn {
  classProperty: string; //field on the class
  step?: string; //used for numper inputs
  canEdit?: boolean; //used to determine if the field can be when adding inline
  currency?: string; //specify currency when your input type is currency
  currencyCol?: string; //specify currency column when your input type is currency
  currencyFormat?: string = '1.0-0'; //specify currency format when your input type is currency
  maxLength?: string | number | null; //configures the max lenght of characters
  icon?: string; // used when we show an icon instead of a value, it is hidden IF value is null
  type?: ColumnType; //determines what input component will be used
  isRequired?: string | boolean; //used to check when inline editing or adding a new object
  copyOption?: boolean; //shows a copy icon next to field content that allows the user to copy the content
  copyIconOnly?: boolean;
  displayValue?: string; //used for the date input, specifying this option uses our custom date input with the specify format (pass in a field with the desired format)
  enableInlineEdit?: boolean; //determines if a field can be editable when the users want to edit a row
  minWidth?: string; //specifies the min width of the column eg '100px'
  maxWidth?: string; //specifies the max width of the column eg '100px'
  options?: { value: any; displayValue: string }[] = []; //used for when input type is SELECT 
  customColor?: string; //adds specified color to column
  shrinkCell?: boolean; //determines if the column can shrink to fit-content
  url?: string; // to provide optional url to open on click, not displayed
  callback?: any; // options callback for buttons
  buttons?: ICustomButton[] = [];
  linkCallback?: any; // makes a column a link that executes callback on click
  nowrap?: boolean = true;
  arrayProperty?: string; // used for text-array input type to indicate which field to show from the array
  arrayProperties?: string[]; // used for text-array input type to indicate multiple fields per array item
  dateFormat?: string;
  showWarningIcon?: boolean = false; // used to show warning icon beside the cell content
}

export interface IItemChange {
  index: number;
  oldValue: any;
  newValue: any;
}

export interface ICustomButton {
  label: string;
  click: any;
  cssClass?: string;
  disabled?: boolean; // used in loading page
  helpText?: string; // shows help icon with click handler to display help text
}


export interface ITableFilter {
  values: string[];
  labels?: string[]; // if not provided, will use values as labels
  default?: string; // initially selected value
  unselectedLabel?: string; // only needed if you can have no filter selected
  field: string;
  partialMatch?: boolean; // if true, will match any value that contains the filter value
  changeCallback?: Function; // like a callback to apply additional filtering
  matchFunc?: string; // function name on objects to call to check if the object matches filter, should take a string
}
